<template>
  <div class="play-earworm-container">
    <Header :title="earworm?.name || 'Play Earworm'" :user="currentUser" />
    
    <main class="page-main">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading earworm...</p>
      </div>
      
      <div v-else-if="error" class="error-state">
        <div class="error-icon">⚠️</div>
        <h3>Error Loading Earworm</h3>
        <p>{{ error }}</p>
      </div>
      
      <div v-else-if="earworm">
        <!-- Audio Player Controls -->
        <div class="player-container">
          <div class="audio-controls">
            <div class="audio-info">
              <h2>{{ earworm.name }}</h2>
              <p v-if="isPlaying">Elapsed: {{ formatTime(currentTime) }}</p>
            </div>
            
            <audio 
              ref="audioPlayer"
              :src="currentAudioUrl"
              @loadedmetadata="onAudioLoaded"
              @timeupdate="onTimeUpdate"
              @ended="onAudioEnded"
              @play="isPlaying = true"
              @pause="isPlaying = false"
              preload="metadata"
            />
            
            <!-- Progress Bar -->
            <div v-if="currentDuration" class="progress-container">
              <div class="progress-bar" @click="seekTo">
                <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
                <div class="progress-handle" :style="{ left: progressPercent + '%' }"></div>
              </div>
              <div class="time-display">
                <span>{{ formatTime(currentTime) }}</span>
                <span>{{ formatTime(currentDuration) }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Lyrics Display -->
        <div class="lyrics-container">
          <div v-if="hasTimestampedLyrics && lyricsLines.length" class="simple-lyrics-display">
            <div class="lyrics-content">
              <h3>Lyrics</h3>
              <p class="lyrics-subtitle">Track {{ selectedSong }}</p>
              <div class="all-lines-container">
                <div
                  v-for="(line, lineIndex) in lyricsLines"
                  :key="`line-${lineIndex}`"
                  :class="['lyrics-line', { active: lineIndex === currentLineIndex }]"
                >
                  <span
                    v-for="(word, wordIndex) in line.words"
                    :key="`line-${lineIndex}-word-${wordIndex}`"
                    :class="[
                      'word',
                      {
                        active: isWordActive(word),
                        completed: isWordCompleted(word),
                        'spanish-word': word.language === 'spanish'
                      }
                    ]"
                  >
                    {{ word.word }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="no-lyrics">
            <p>No timestamped lyrics available for this track yet.</p>
          </div>
        </div>

        <div class="bottom-controls">
          <div class="track-controls">
            <button
              @click="playTrack(1)"
              class="track-btn"
              :class="{ active: selectedSong === 1 }"
              :disabled="!earworm?.suno_audio_url"
            >
              <span class="play-icon"></span>
              <span class="track-label">Track 1</span>
            </button>

            <button 
              @click="playTrack(2)" 
              class="track-btn"
              :class="{ active: selectedSong === 2 }"
              :disabled="!earworm?.suno_audio_url2"
            >
              <span class="play-icon"></span>
              <span class="track-label">Track 2</span>
            </button>
            
            <button @click="togglePause" class="pause-btn" :disabled="!audioPlayer || currentDuration === 0">
              <span class="pause-icon" v-if="isPlaying">⏸</span>
              <span class="play-icon" v-else>▶</span>
            </button>
            
            <button @click="stopAudio" class="stop-btn" :disabled="!isPlaying">
              <span class="stop-icon"></span>
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import Header from '../components/Header.vue'

const router = useRouter()
const route = useRoute()

const currentUser = ref<any>(null)
const loading = ref(true)
const error = ref('')
const earworm = ref<any>(null)
const selectedSong = ref(1)
const isPlaying = ref(false)
const currentTime = ref(0)
const currentDuration = ref(0)
const currentLineIndex = ref(-1)
const audioPlayer = ref<HTMLAudioElement | null>(null)

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'

// Computed properties
const currentAudioUrl = computed(() => {
  if (!earworm.value) return ''
  return selectedSong.value === 1 ? earworm.value.suno_audio_url : earworm.value.suno_audio_url2
})

const currentTimestampedLyrics = computed(() => {
  if (!earworm.value) return null;
  const lyricsData = selectedSong.value === 1 ? earworm.value.suno_ts_lyrics : earworm.value.suno_ts_lyrics2;

  // If lyricsData has .data.alignedWords, flatten it for compatibility
  if (lyricsData && lyricsData.data && Array.isArray(lyricsData.data.alignedWords)) {
    return { ...lyricsData, alignedWords: lyricsData.data.alignedWords };
  }
  // Existing logic for string or already-flattened object
  if (typeof lyricsData === 'string') {
    try {
      const parsed = JSON.parse(lyricsData);
      if (parsed && parsed.data && Array.isArray(parsed.data.alignedWords)) {
        return { ...parsed, alignedWords: parsed.data.alignedWords };
      }
      return parsed;
    } catch (error) {
      console.error('❌ Failed to parse lyrics string for song', selectedSong.value, ':', error);
      return null;
    }
  }
  if (lyricsData && typeof lyricsData === 'object') {
    // If already has alignedWords at root
    return lyricsData;
  }
  return null;
})

const hasTimestampedLyrics = computed(() => {
  const lyrics = currentTimestampedLyrics.value
  const hasWords = !!lyrics?.alignedWords?.length
  console.log('🎵 Has timestamped lyrics:', hasWords, 'Words count:', lyrics?.alignedWords?.length || 0)
  
  // Debug: Log first few words to verify timestamp structure
  if (hasWords && lyrics.alignedWords.length > 0) {
    console.log('🔍 First 3 words sample:', lyrics.alignedWords.slice(0, 3).map((w: any) => ({
      word: w.word,
      cleaned: cleanLyricsText(w.word),
      start: w.start ?? w.startS,
      end: w.end ?? w.endS
    })))
  }
  
  return hasWords
})

const progressPercent = computed(() => {
  if (!currentDuration.value) return 0
  return (currentTime.value / currentDuration.value) * 100
})

const detectWordLanguage = (word: string): 'spanish' | 'english' | null => {
  if (!word) return null
  const lower = word.toLowerCase()
  if (lower.includes('[spanish')) return 'spanish'
  if (lower.includes('[english')) return 'english'
  return null
}

// Enhanced function to clean lyrics text - removes ALL bracket tags
const cleanLyricsText = (word: string): string => {
  if (!word) return ''
  
  // Remove ALL bracket tags: [spanish], [english], etc.
  return word
    .replace(/\[spanish\]/gi, '')
    .replace(/\[english\]/gi, '')
    .replace(/\[[^\]]+\]/g, '') // Remove any other [tag] patterns
    .replace(/[""]/g, '"') // Normalize quotes
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim()
}

// Process timestamped lyrics into lines with cleaned text
const lyricsLines = computed(() => {
  if (!hasTimestampedLyrics.value) {
    console.log('❌ No timestamped lyrics available')
    return []
  }
  
  const words = currentTimestampedLyrics.value.alignedWords || []
  if (words.length === 0) {
    console.log('❌ No aligned words found')
    return []
  }
  
  console.log('🎵 Processing', words.length, 'words for karaoke display')
  
  // First pass: clean and filter words
  const cleanedWords: any[] = []
  let currentLanguage: 'spanish' | 'english' | null = null
  
  words.forEach((word: any, index: number) => {
    const rawWord = word.word || ''
    const startTime = word.start ?? word.startS ?? word.s
    const endTime = word.end ?? word.endS ?? word.e
    const tagLanguage = detectWordLanguage(rawWord)
    const cleanedText = cleanLyricsText(rawWord)

    // If this token is just a language directive, update context and skip
    if (!cleanedText) {
      if (tagLanguage) {
        currentLanguage = tagLanguage
        return
      }
      console.warn('⚠️ Skipping empty word at index', index, 'Original:', rawWord)
      return
    }

    // Update context if word carried an explicit tag while still having text
    if (tagLanguage) {
      currentLanguage = tagLanguage
    }

    const language = tagLanguage || currentLanguage
    
    cleanedWords.push({
      word: cleanedText,
      start: startTime ?? 0,
      end: endTime ?? (startTime ?? 0) + 0.5,
      originalWord: rawWord, // Keep original for debugging
      language
    })
  })
  
  console.log('✅ Cleaned words:', cleanedWords.length, 'from', words.length, 'original words')
  
  // Second pass: create lines with better phrase detection
  const lines: Array<{ words: any[], startTime: number, endTime: number }> = []
  let currentLine: any[] = []
  let lineStartTime = 0
  
  cleanedWords.forEach((word: any, index: number) => {
    if (currentLine.length === 0) {
      lineStartTime = word.start
    }
    
    currentLine.push(word)
    
    // Improved line breaking logic
    const isLastWord = index === cleanedWords.length - 1
    const wordText = word.word.toLowerCase()
    
    // Detect phrase boundaries
    const endsWithQuote = word.word.endsWith('"')
    const hasIsPattern = wordText.includes(' is ')
    const hasMeansPattern = wordText.includes(' means ')
    const hasLineBreak = word.word.includes('\n')
    
    // Break on natural phrase boundaries
    const endsPhrase = endsWithQuote || hasIsPattern || hasMeansPattern || hasLineBreak
    const isLongLine = currentLine.length >= 10
    
    const shouldBreakLine = isLastWord || endsPhrase || isLongLine
    
    if (shouldBreakLine && currentLine.length > 0) {
      const lineEndTime = word.end || word.start + 1
      lines.push({
        words: [...currentLine],
        startTime: lineStartTime,
        endTime: lineEndTime
      })
      
      if (index < cleanedWords.length - 1) {
        console.log(`📝 Line ${lines.length}: "${currentLine.map(w => w.word).join(' ')}" (${lineStartTime.toFixed(2)}s - ${lineEndTime.toFixed(2)}s)`)
      }
      
      currentLine = []
    }
  })
  
  console.log(`✅ Created ${lines.length} lyric lines from ${cleanedWords.length} cleaned words`)
  if (lines.length > 0) {
    console.log('📋 First line starts at:', lines[0]?.startTime.toFixed(2), 'seconds')
    console.log('📋 Last line ends at:', lines[lines.length - 1]?.endTime.toFixed(2), 'seconds')
  }
  
  return lines
})

// Audio control functions
const playTrack = (trackNumber: number) => {
  console.log('🎵 Switching to track:', trackNumber)
  
  if (trackNumber === selectedSong.value && isPlaying.value) {
    // If same track is playing, pause it
    audioPlayer.value?.pause()
    return
  }
  
  // Stop current audio if playing different track
  if (audioPlayer.value) {
    audioPlayer.value.pause()
    audioPlayer.value.currentTime = 0
  }
  
  selectedSong.value = trackNumber
  currentTime.value = 0
  currentLineIndex.value = -1
  
  console.log('🎵 Selected song changed to:', selectedSong.value)
  console.log('🎵 New audio URL available:', !!currentAudioUrl.value)
  console.log('🎵 New lyrics available:', hasTimestampedLyrics.value)
  
  // Start playing the selected track after a brief delay to allow URL to update
  setTimeout(() => {
    if (audioPlayer.value && currentAudioUrl.value) {
      audioPlayer.value.load() // Reload with new source
      audioPlayer.value.play().catch(error => {
        console.error('Failed to play audio:', error)
      })
    } else {
      console.error('❌ Cannot play: audioPlayer or currentAudioUrl not available')
    }
  }, 200)
}

const togglePause = () => {
  if (!audioPlayer.value) return
  
  if (isPlaying.value) {
    audioPlayer.value.pause()
  } else {
    audioPlayer.value.play().catch(error => {
      console.error('Failed to play audio:', error)
    })
  }
}

const stopAudio = () => {
  if (!audioPlayer.value) return
  
  audioPlayer.value.pause()
  audioPlayer.value.currentTime = 0
  currentTime.value = 0
  currentLineIndex.value = -1
}

const seekTo = (event: MouseEvent) => {
  if (!audioPlayer.value || !currentDuration.value) return
  
  const progressBar = event.currentTarget as HTMLElement
  const rect = progressBar.getBoundingClientRect()
  const clickX = event.clientX - rect.left
  const percent = clickX / rect.width
  const newTime = percent * currentDuration.value
  
  audioPlayer.value.currentTime = newTime
  currentTime.value = newTime
}

// Audio event handlers
const onAudioLoaded = () => {
  if (audioPlayer.value) {
    currentDuration.value = audioPlayer.value.duration || 0
    console.log('🎵 Audio loaded, duration:', formatTime(currentDuration.value))
  }
}

const onTimeUpdate = () => {
  if (audioPlayer.value) {
    currentTime.value = audioPlayer.value.currentTime
    updateCurrentLine()
  }
}

const onAudioEnded = () => {
  isPlaying.value = false
  currentTime.value = 0
  currentLineIndex.value = -1
  if (audioPlayer.value) {
    audioPlayer.value.currentTime = 0
  }
}

// Lyrics synchronization with improved timing
const updateCurrentLine = () => {
  if (!hasTimestampedLyrics.value || lyricsLines.value.length === 0) {
    return
  }
  
  const currentTimeMs = currentTime.value
  
  // Always start with first line when playing begins
  if (isPlaying.value && currentLineIndex.value === -1 && lyricsLines.value.length > 0) {
    currentLineIndex.value = 0
    console.log('🔧 Setting to first line on play start')
    return
  }
  
  // Find the correct line based on timing with lookahead
  for (let i = 0; i < lyricsLines.value.length; i++) {
    const line = lyricsLines.value[i]
    if (!line) continue
    
    // Use lookahead for smoother display (2 seconds early)
    const lineStart = line.startTime - 2.0
    const nextLineStart = i < lyricsLines.value.length - 1 ? 
      (lyricsLines.value[i + 1]?.startTime ?? line.endTime + 5) : 
      line.endTime + 5 // Show last line for 5 extra seconds
    
    if (currentTimeMs >= lineStart && currentTimeMs < nextLineStart) {
      if (currentLineIndex.value !== i) {
        currentLineIndex.value = i
        console.log(`🎤 Line ${i + 1}/${lyricsLines.value.length}: "${line.words.map(w => w.word).join(' ')}"`)
      }
      return
    }
  }
  
  // If we're before any line starts, show the first line if we're within 10 seconds of it
  if (lyricsLines.value.length > 0 && lyricsLines.value[0] && currentTimeMs < lyricsLines.value[0].startTime) {
    const timeToFirstLine = lyricsLines.value[0].startTime - currentTimeMs
    if (timeToFirstLine <= 10 && currentLineIndex.value !== 0) {
      currentLineIndex.value = 0
      console.log('🔧 Early display of first line, starts in', timeToFirstLine.toFixed(1), 'seconds')
    }
  }
}

const isWordActive = (word: any) => {
  if (!word || typeof word.start !== 'number' || typeof word.end !== 'number') return false
  const time = currentTime.value
  return time >= word.start && time <= word.end
}

const isWordCompleted = (word: any) => {
  if (!word || typeof word.end !== 'number') return false
  return currentTime.value > word.end
}

// Utility functions
const formatTime = (seconds: number) => {
  if (!seconds || isNaN(seconds)) return '0:00'
  
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Load earworm data
const loadEarworm = async () => {
  try {
    loading.value = true
    error.value = ''
    
    const earwormId = route.params.id as string
    console.log('🎵 Loading earworm for playback, ID:', earwormId)
    
    if (!earwormId || earwormId === 'undefined' || earwormId === 'null') {
      throw new Error('Invalid earworm ID')
    }
    
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    console.log('📡 Fetching earworm data from API...')
    const response = await fetch(`${API_BASE_URL}/api/earworms/${earwormId}`, { headers })
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Earworm not found')
      }
      throw new Error(`Server error: ${response.status}`)
    }
    
    const data = await response.json()
    console.log('📡 Received earworm data for player')
    
    // Log the timestamped lyrics data structure for debugging
    if (data.suno_ts_lyrics) {
      console.log('🎵 Song 1 timestamped lyrics available:', !!data.suno_ts_lyrics.alignedWords)
    }
    if (data.suno_ts_lyrics2) {
      console.log('🎵 Song 2 timestamped lyrics available:', !!data.suno_ts_lyrics2.alignedWords)
    }
    
    earworm.value = data
    
    // Auto-select song 1 if available, or song 2 if song 1 is not available
    if (data.suno_audio_url) {
      selectedSong.value = 1
      console.log('🎵 Selected song 1 for playback')
    } else if (data.suno_audio_url2) {
      selectedSong.value = 2
      console.log('🎵 Selected song 2 for playback')
    } else {
      error.value = 'No audio available for this earworm'
      console.log('❌ No audio URLs found in earworm data')
      return
    }
    
    console.log('✅ Earworm loaded for playback:', data.name)
    
  } catch (err) {
    console.error('❌ Failed to load earworm:', err)
    error.value = err instanceof Error ? err.message : 'Failed to load earworm'
  } finally {
    loading.value = false
  }
}

// Watch for audio URL changes to reset player
watch(currentAudioUrl, (newUrl, oldUrl) => {
  console.log('🔄 Audio URL changed')
  currentTime.value = 0
  currentDuration.value = 0
  currentLineIndex.value = -1
  
  if (audioPlayer.value && newUrl) {
    audioPlayer.value.load()
  }
})

// Watch for song selection changes to reset lyrics
watch(selectedSong, (newSong, oldSong) => {
  console.log('🎵 Song selection changed from', oldSong, 'to', newSong)
  currentTime.value = 0
  currentLineIndex.value = -1
})

onMounted(async () => {
  // Load user data
  try {
    const userStr = sessionStorage.getItem('currentUser')
    if (userStr) {
      currentUser.value = JSON.parse(userStr)
    }
  } catch (error) {
    console.error('Error parsing user data:', error)
  }
  
  await loadEarworm()
})

onUnmounted(() => {
  // Cleanup audio if playing
  if (audioPlayer.value) {
    audioPlayer.value.pause()
  }
})
</script>

<style scoped>
.play-earworm-container {
  min-height: 100vh;
  background: #f8f9fa;
}

.page-main {
  padding: 1rem;
  max-width: 900px;
  margin: 0 auto;
  padding-bottom: 10rem;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 3rem 1rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #43a047;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.player-container {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
}

.audio-controls {
  margin-bottom: 1rem;
  text-align: center;
}

.audio-info h2 {
  margin: 0 0 0.5rem 0;
  color: #333;
}

.audio-info p {
  margin: 0 0 1.5rem 0;
  color: #666;
  font-size: 0.95rem;
}

/* Progress bar styles */
.progress-container {
  margin: 1.5rem 0 0.5rem 0;
}

.progress-bar {
  position: relative;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: #007bff;
  border-radius: 4px;
  transition: width 0.1s ease;
}

.progress-handle {
  position: absolute;
  top: -4px;
  width: 16px;
  height: 16px;
  background: #007bff;
  border-radius: 50%;
  transform: translateX(-50%);
  transition: left 0.1s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.time-display {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  color: #666;
  font-family: monospace;
}

/* Lyrics Container */
.lyrics-container {
  background: white;
  border-radius: 12px;
  margin: 1rem auto 2rem auto;
  max-width: 900px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  padding: 0;
}

.no-lyrics {
  text-align: center;
  color: #666;
  padding: 2rem;
}

.simple-lyrics-display {
  padding: 0;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
}

.lyrics-content h3 {
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 1.5rem;
}

.lyrics-subtitle {
  margin: 0 0 1.5rem 0;
  color: #6c757d;
  font-size: 0.95rem;
  font-weight: 500;
}

.all-lines-container {
  max-height: 60vh;
  overflow-y: auto;
  padding: 0;
  background: #f8f9fa;
  border-radius: 8px;
}

.lyrics-line {
  margin: 0;
  padding: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  transition: all 0.3s ease;
}

.lyrics-line.active {
  background: rgba(0, 123, 255, 0.1);
  border-left: none;
  padding-left: 0;
  transform: scale(1.02);
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
}

.word {
  display: inline;
  margin-right: 0.35rem;
  color: #333;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  border-radius: 4px;
}

.word.active {
  color: #007bff;
}

.word.spanish-word {
  color: #c62828;
}

.word.active.spanish-word {
  color: #c62828;
}

.word.completed {
  color: #28a745;
  opacity: 0.75;
  font-weight: 500;
}

/* Bottom controls */
.bottom-controls {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 2px solid #e0e0e0;
  padding: 1.25rem 1rem;
  box-shadow: 0 -2px 12px rgba(0,0,0,0.1);
  z-index: 1000;
}

.track-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  max-width: 700px;
  margin: 0 auto;
}

.track-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.25rem;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;
  min-height: 60px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.track-btn:hover:not(:disabled) {
  background: #218838;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.track-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  opacity: 0.6;
}

.track-btn.active {
  background: #007bff;
  box-shadow: 0 0 15px rgba(0, 123, 255, 0.5);
}

.pause-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.875rem 1.25rem;
  background: #ffc107;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;
  min-height: 60px;
  font-size: 1.5rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.pause-btn:hover:not(:disabled) {
  background: #e0a800;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.pause-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  opacity: 0.6;
}

.stop-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.875rem 1.25rem;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;
  min-height: 60px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.stop-btn:hover:not(:disabled) {
  background: #c82333;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.stop-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  opacity: 0.6;
}

.play-icon,
.stop-icon {
  display: inline-block;
}

.play-icon {
  width: 0;
  height: 0;
  border-left: 14px solid white;
  border-top: 9px solid transparent;
  border-bottom: 9px solid transparent;
}

.stop-icon {
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 2px;
}

.track-label {
  font-size: 0.95rem;
  letter-spacing: 0.5px;
}

/* Scrollbar styling */
.all-lines-container::-webkit-scrollbar {
  width: 8px;
}

.all-lines-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.all-lines-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.all-lines-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Responsive design */
@media (max-width: 768px) {
  .page-main {
    padding: 0.5rem;
    padding-bottom: 8rem;
  }
  
  .player-container {
    padding: 1.5rem;
  }
  
  .lyrics-container {
    margin: 0.5rem;
    padding: 0;
  }
  
  .track-controls {
    gap: 0.75rem;
  }
  
  .track-btn,
  .pause-btn,
  .stop-btn {
    min-width: 70px;
    min-height: 55px;
    padding: 0.75rem 1rem;
  }
  
  .track-label {
    font-size: 0.85rem;
  }
  
  .word {
    font-size: 0.85rem;
    margin-right: 0.3rem;
  }
}

@media (max-width: 480px) {
  .lyrics-content h3 {
    font-size: 1.3rem;
  }
  
  .word {
    font-size: 0.8rem;
    margin-right: 0.25rem;
  }
  
  .track-btn,
  .pause-btn,
  .stop-btn {
    min-width: 60px;
    min-height: 50px;
    padding: 0.65rem 0.85rem;
  }
  
  .track-label {
    font-size: 0.8rem;
  }
}
</style>